import { format } from "date-fns";
import taskService from "../services/taskService";


export const fetchAllDayTasks = async (days, setTasksByDay) => {
    // *** Declare tasksByDayMap outside the try block ***
    const tasksByDayMap = {};
    try {
      // const tasksByDayMap = {}; // Remove declaration from here

      for (const day of days) {
        const formattedDate = format(new Date(day), "yyyy-MM-dd");
        const result = await taskService.getTasksByDay(formattedDate);
        //console.log(`Tasks for ${formattedDate} fetched:`, result.tasks); // Log fetched tasks

        // *** Deduplicate tasks based on ID ***
        const uniqueTasks = [];
        const seenIds = new Set();
        if (Array.isArray(result.tasks)) {
          result.tasks.forEach((task) => {
            if (task && task.id && !seenIds.has(task.id)) {
              uniqueTasks.push(task);
              seenIds.add(task.id);
            } else if (task && task.id && seenIds.has(task.id)) {
              console.warn(
                `[fetchAllDayTasks] Duplicate task ID ${task.id} found for date ${formattedDate}. Skipping.`
              );
            } else if (!task || !task.id) {
              console.warn(
                `[fetchAllDayTasks] Invalid task object found for date ${formattedDate}:`,
                task
              );
            }
          });
        } else {
          console.warn(
            `[fetchAllDayTasks] result.tasks for ${formattedDate} is not an array or is null/undefined:`,
            result.tasks
          );
        }
        // *** End Deduplication ***

        //console.log(`Unique tasks for ${formattedDate}:`, uniqueTasks); // Log unique tasks
        tasksByDayMap[formattedDate] = uniqueTasks; // Use unique tasks
      }

      //console.log("Setting tasksByDay:", tasksByDayMap);
      setTasksByDay(tasksByDayMap);
    } catch (error) {
      console.error("Error fetching tasks for calendar days:", error);
      // In case of error, we'll just set the map with what we've collected so far
      setTasksByDay(tasksByDayMap);
    }
  };

export const getTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    slots.push(`${hour.toString().padStart(2, "0")}:00`);
  }
  return slots;
};

// Remove useCallback from calculateTaskPosition
export const calculateTaskPosition = (task, timeSlot) => {
  // For ongoing tasks (has start_time but no end_time), use a default duration of 2 hours
  if (task.start_time && !task.end_time) {
    let startHour,
      startMinutes = 0;
    try {
      // Parse start time
      if (task.start_time.includes("T")) {
        const startDate = new Date(task.start_time);
        startHour = startDate.getUTCHours();
        startMinutes = startDate.getUTCMinutes();
      } else {
        // Parse simple time format "HH:MM:SS"
        const parts = task.start_time.split(":").map(Number);
        startHour = parts[0];
        startMinutes = parts[1] || 0;
      }

      const timeSlotHour = parseInt(timeSlot.split(":")[0]);

      // For ongoing tasks, set a fixed duration of 2 hours
      return {
        isFirstHour: timeSlotHour === startHour,
        duration: 2, // 2 hours duration for ongoing tasks
        startHour: startHour,
        startMinutes: startMinutes,
        endHour: startHour + 2, // Add 2 hours as default
        endMinutes: startMinutes,
        isOngoing: true, // Flag to indicate this is an ongoing task
      };
    } catch (e) {
      console.error("Error calculating position for ongoing task:", e);
      return {
        isFirstHour: true,
        duration: 2,
        startMinutes: 0,
        isOngoing: true,
      };
    }
  }

  if (!task.start_time)
    return { isFirstHour: true, duration: 1, startMinutes: 0 }; // Default 1 hour for tasks without specific time

  let startHour, endHour;
  let startMinutes = 0,
    endMinutes = 0;
  try {
    // Parse start time
    if (task.start_time.includes("T")) {
      const startDate = new Date(task.start_time);
      startHour = startDate.getUTCHours();
      startMinutes = startDate.getUTCMinutes();

      // Parse end time
      if (task.end_time && task.end_time.includes("T")) {
        const endDate = new Date(task.end_time);
        endHour = endDate.getUTCHours();
        endMinutes = endDate.getUTCMinutes();

        // If end time is earlier than start time (should not happen normally), default to 1 hour
        if (
          endHour < startHour ||
          (endHour === startHour && endMinutes <= startMinutes)
        ) {
          endHour = startHour + 1;
          endMinutes = startMinutes;
        }
      } else {
        // If no end time, assume 1 hour duration
        endHour = startHour + 1;
        endMinutes = startMinutes;
      }
    } else {
      // Parse simple time format "HH:MM:SS"
      const parts = task.start_time.split(":").map(Number);
      startHour = parts[0];
      startMinutes = parts[1] || 0;

      // Parse end time
      if (task.end_time) {
        const endParts = task.end_time.split(":").map(Number);
        endHour = endParts[0];
        endMinutes = endParts[1] || 0;

        // If end time is same or earlier, default to 1 hour duration
        if (
          endHour < startHour ||
          (endHour === startHour && endMinutes <= startMinutes)
        ) {
          endHour = startHour + 1;
          endMinutes = startMinutes;
        }
      } else {
        // If no end time, assume 1 hour duration
        endHour = startHour + 1;
        endMinutes = startMinutes;
      }
    }

    // Calculate hours duration with minute precision
    let duration = endHour - startHour + (endMinutes - startMinutes) / 60;

    // Ensure minimum duration (at least 15 minutes = 0.25 hours)
    if (duration <= 0) {
      duration = 0.25;
    }

    const timeSlotHour = parseInt(timeSlot.split(":")[0]);

    // Return position data for this time slot
    return {
      isFirstHour: timeSlotHour === startHour,
      duration: duration,
      startHour: startHour,
      startMinutes: startMinutes,
      endHour: endHour,
      endMinutes: endMinutes,
    };
  } catch (e) {
    console.error("Error calculating task position:", e);
    return { isFirstHour: true, duration: 1, startMinutes: 0 }; // Fallback to 1 hour
  }
};

// Add a helper function to group overlapping tasks into columns
export const assignTasksToColumns = (tasks) => {
  if (!tasks || tasks.length <= 1)
    return tasks.map((task) => ({
      ...task,
      column: 0,
      totalColumns: 1,
      isFullWidth: true,
    }));

  //console.log("Initial tasks:", tasks);

  // Convert tasks to a more consistent format with decimal times
  const normalizedTasks = tasks.map((task) => {
    let startTime, endTime;

    // Parse start time
    if (task.start_time) {
      if (task.start_time.includes("T")) {
        const startDate = new Date(task.start_time);
        startTime = startDate.getUTCHours() + startDate.getUTCMinutes() / 60;
      } else {
        const [hours, minutes] = task.start_time.split(":").map(Number);
        startTime = hours + minutes / 60;
      }
    } else {
      startTime = 0; // Fallback
    }

    // Parse end time
    if (task.end_time) {
      if (task.end_time.includes("T")) {
        const endDate = new Date(task.end_time);
        endTime = endDate.getUTCHours() + endDate.getUTCMinutes() / 60;
      } else {
        const [hours, minutes] = task.end_time.split(":").map(Number);
        endTime = hours + minutes / 60;
      }
    } else if (task.start_time) {
      endTime = startTime + 1; // Default 1 hour
    } else {
      endTime = 1; // Fallback
    }

    // Ensure minimum duration (15 minutes)
    if (endTime - startTime < 0.25) {
      endTime = startTime + 0.25;
    }

    return {
      ...task,
      start_decimal: startTime,
      end_decimal: endTime,
    };
  });

  // Sort tasks by start time
  normalizedTasks.sort((a, b) => a.start_decimal - b.start_decimal);


  // Build an overlap matrix (which tasks overlap with which)
  const overlapMatrix = {};
  normalizedTasks.forEach((task) => {
    overlapMatrix[task.id] = new Set();
  });

  // Fill the overlap matrix by checking each pair of tasks
  for (let i = 0; i < normalizedTasks.length; i++) {
    const taskA = normalizedTasks[i];

    for (let j = i + 1; j < normalizedTasks.length; j++) {
      const taskB = normalizedTasks[j];

      // Check if tasks overlap (with a small buffer for floating point precision)
      const buffer = 0.001; // ~3.6 seconds
      if (
        taskA.start_decimal < taskB.end_decimal + buffer &&
        taskA.end_decimal > taskB.start_decimal - buffer
      ) {
        // Mark both tasks as overlapping with each other
        overlapMatrix[taskA.id].add(taskB.id);
        overlapMatrix[taskB.id].add(taskA.id);
 
      }
    }
  }

  // Find connected components (groups of tasks that overlap directly or indirectly)
  const visited = new Set();
  const overlapGroups = [];

  // DFS to find all connected tasks
  const findConnectedTasks = (taskId, group) => {
    visited.add(taskId);
    group.push(taskId);

    overlapMatrix[taskId].forEach((overlappingId) => {
      if (!visited.has(overlappingId)) {
        findConnectedTasks(overlappingId, group);
      }
    });
  };

  // Process each task to find its group
  normalizedTasks.forEach((task) => {
    if (!visited.has(task.id)) {
      const newGroup = [];
      findConnectedTasks(task.id, newGroup);
      overlapGroups.push(newGroup);
    }
  });

  //console.log("[assignTasksToColumns] Overlap groups:", overlapGroups);

  // Now assign columns within each overlap group
  const tasksWithColumns = [];

  // Process each group
  overlapGroups.forEach((group, groupIndex) => {


    // Get the actual task objects for this group
    const groupTasks = normalizedTasks.filter((task) =>
      group.includes(task.id)
    );

    if (groupTasks.length === 1) {
      // If only one task in the group, it gets full width
      const task = groupTasks[0];
      tasksWithColumns.push({
        ...task,
        column: 0,
        totalColumns: 1,
        isFullWidth: true,
        groupIndex,
      });

      return; // Continue to next group
    }

    // Sort by start time for column assignment
    groupTasks.sort((a, b) => a.start_decimal - b.start_decimal);

    // Track end times for each column in this group
    const columnEndTimes = [];

    // Assign each task to the first available column
    const groupTasksWithColumns = groupTasks.map((task) => {
      // Find the first column where this task doesn't overlap
      let columnIndex = 0;
      const overlapBuffer = 0.001; // ~3.6 seconds buffer

     

      while (columnIndex < columnEndTimes.length) {
        // If this column's last task ends before or at this task's start time, we can use it
        if (
          columnEndTimes[columnIndex] <=
          task.start_decimal + overlapBuffer
        ) {
          break;
        }
        columnIndex++;
      }

      // Update the end time for this column
      columnEndTimes[columnIndex] = task.end_decimal;

   

      return {
        ...task,
        column: columnIndex,
        isFullWidth: false,
        groupIndex,
      };
    });

    // Count columns used in this group
    const columnsUsed =
      Math.max(...groupTasksWithColumns.map((t) => t.column)) + 1;
   

    // Update all tasks with the total columns for this group
    groupTasksWithColumns.forEach((task) => {
      tasksWithColumns.push({
        ...task,
        totalColumns: columnsUsed,
      });
    });
  });

  

  return tasksWithColumns;
};

export const getTaskStyles = (taskSize) => {
    switch (taskSize.type) {
      case "normal": // > 45 mins
        return {
          padding: "p-1.5",
          titleFontSize: "text-base",
          timeFontSize: "text-xs",
          categoryFontSize: "text-xs",
          checkIconSize: "h-3.5 w-3.5",
          buttonPadding: "p-1",
          buttonMargin: "ml-1",
          showTime: true,
          showCategory: true,
          showCheckbox: true,
          showOngoingIndicator: true,
        };
      case "medium": // 30-45 mins
        return {
          padding: "p-1",
          titleFontSize: "text-base",
          timeFontSize: "text-xs",
          categoryFontSize: "text-xs",
          checkIconSize: "h-3 w-3",
          buttonPadding: "p-0.5",
          buttonMargin: "ml-0.5",
          showTime: true,
          showCategory: true,
          showCheckbox: true,
          showOngoingIndicator: true,
        };
      case "small": // 15-30 mins
        return {
          padding: "px-1 py-0.5",
          titleFontSize: "text-[16px]",
          timeFontSize: "text-[12px]",
          categoryFontSize: "text-[12px]",
          checkIconSize: "h-2.5 w-2.5",
          buttonPadding: "p-0.5",
          buttonMargin: "ml-0.5",
          showTime: true,
          showCategory: false,
          showCheckbox: true,
          showOngoingIndicator: true,
          centerContent: true,
        };
      case "extraSmall": // < 15 mins
        return {
          padding: "px-0.5 py-0",
          titleFontSize: "text-[12px]",
          timeFontSize: "text-[8px]",
          categoryFontSize: "text-[8px]",
          checkIconSize: "h-2 w-2",
          buttonPadding: "p-0.5",
          buttonMargin: "ml-0.5",
          showTime: false,
          showCategory: false,
          showCheckbox: true,
          showOngoingIndicator: false,
          centerContent: true,
        };
      default:
        return {
          padding: "p-1.5",
          titleFontSize: "text-[12px]",
          timeFontSize: "text-[10px]",
          categoryFontSize: "text-[9px]",
          checkIconSize: "h-3.5 w-3.5",
          buttonPadding: "p-1",
          buttonMargin: "ml-1",
          showTime: true,
          showCategory: true,
          showCheckbox: true,
          showOngoingIndicator: true,
        };
    }
  };

  export const handleNoteClick = async (task, event, createNote) => {
    event.stopPropagation(); // Prevent triggering task edit


    if (task.notes_link) {
      // Open existing note in a new tab
      // Check if the notes_link is already a full URL or contains the /notes/ prefix
      const url =
        task.notes_link.includes("://") || task.notes_link.startsWith("/notes/")
          ? task.notes_link
          : `/notes/${task.notes_link}`;
      window.open(url, "_blank");
    } else {
      try {
        // Create a new note
        const newNote = await createNote({
          title: `Note for: ${task.title}`,
          content: `# ${task.title}\n\nTask details:\n- Date: ${
            task.scheduled_date || task.due_date
          }\n- Category: ${task.category?.name || "None"}\n\n`,
        });

        // Connect the note to the task
        await taskService.connectNoteToTask(task.id, newNote.id);

        // Open the new note in a new tab
        window.open(`/notes/${newNote.id}`, "_blank");
      } catch (error) {
        console.error("Error creating note:", error);
      }
    }
  };

export const updateSidebarTasks = async (activeTab, setTasks) => {
  try {
    const response = await taskService.getTasks({ taskType: activeTab });
    setTasks(response.tasks || []);
  } catch (error) {
    console.error("Error fetching tasks:", error);
  }
};




  export const handleToggleTaskCompletion = async (taskId, event, handleFetchTasks, calendarDays, activeTab, setTasks) => {
    // Stop event propagation to prevent drag interference
    event.stopPropagation();

    try {
      // Call the API to toggle completion
      const updatedTask = await taskService.toggleTaskCompletion(taskId);

      // Refresh calendar tasks
      handleFetchTasks(calendarDays);

      // Refresh sidebar task list
      updateSidebarTasks(activeTab, setTasks);
    } catch (error) {
      console.error("Error toggling task completion:", error);
    }
  };

  export const handleDeleteTask = async (taskId, event, handleFetchTasks, calendarDays, activeTab, setTasks, deleteTask, toast) => {
    if (event) {
      event.stopPropagation();
    }

    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await deleteTask(taskId);
        toast({
          title: "Task deleted successfully",
          variant: "default",
        });

        // Refresh the task list and calendar view
        handleFetchTasks(calendarDays);

        // Also refresh sidebar tasks
        updateSidebarTasks(activeTab, setTasks);
      } catch (error) {
        console.error("Error deleting task:", error);
        toast({
          title: "Failed to delete task",
          variant: "destructive",
        });
      }
    }
  };


  export const reinterpretUtcAsLocalTime = (utcString) => {
    const utcDate = new Date(utcString);
  
    // Reconstruct a new Date using UTC components, treating them as local time
    return new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
      utcDate.getUTCHours(),
      utcDate.getUTCMinutes(),
      utcDate.getUTCSeconds()
    );
  }
  